import { useEffect, useRef, useState } from 'react'
import { labeledXAxis, labeledYAxis } from './helpers/labeledAxes';
import { chartTheme } from './helpers/chartTheme';
import { marginConvention } from './helpers/marginConvention'
import { select, scaleLinear, curveBasis, line, curveCardinal, extent, axisLeft, axisBottom, max } from 'd3';
import AnalysisExport from '../components/AnalysisExport';

function debounce(fn, ms) {
    let timer
    return _ => {
        clearTimeout(timer)
        timer = setTimeout(_ => {
            timer = null
            fn.apply(this, arguments)
        }, ms)
    };
}

const DistanceVsTime = (props) => {
    const [distanceTimeData, setDistanceTimeData] = useState(props.data)
    const [polyTimeData, setPolyTimeData] = useState(props.poly)
    const [polyFunction, setPolyFunction] = useState(props.polyFunction)
    const svgRef = useRef()

    useEffect(() => {
        const totalElapsedTime = distanceTimeData[distanceTimeData.length-1].et

        function responsiveComponent(svg, props) {
            const { width, height, margin } = props;

            svg = svg.enter().append('svg')
                .merge(svg)
                .attr('width', width)
                .attr('height', height);

            const { g, innerWidth, innerHeight } = marginConvention(svg, { width, height, margin });

            const xScale = scaleLinear()
                .domain([0, totalElapsedTime])
                .range([0, innerWidth])
                .nice();

            labeledXAxis(g, Object.assign({}, props, {
                xScale,
                innerWidth,
                innerHeight
            }));

            // convert the distance values to an array
            const distanceArray = distanceTimeData.map((item)=>{
                return item.distance
            })
            
            const yMax = max(distanceArray)
            // console.log('max y value:', yMax)

            const yScale = scaleLinear()
                .domain([0, yMax])
                .range([innerHeight, 0])
                .nice();

            labeledYAxis(g, Object.assign({}, props, {
                yScale,
                innerHeight
            }));
       
            // gridlines in x axis function
            function make_x_gridlines() {		
                return axisBottom(xScale)
                    .ticks()
            }

            // gridlines in y axis function
            function make_y_gridlines() {		
                return axisLeft(yScale)
                    .ticks()
            }

            // add the X gridlines
            g.selectAll('.xgrid').remove()
            g.append("g")
            .attr("class", "xgrid")
            .attr("transform", "translate(0," + innerHeight + ")")
            .call(make_x_gridlines()
                .tickSize(-innerHeight)
                .tickFormat("")
            )

            // add the Y gridlines
            g.selectAll('.ygrid').remove()
            g.append("g")			
            .attr("class", "ygrid")
            .call(make_y_gridlines()
                .tickSize(-innerWidth)
                .tickFormat("")
            )            

            // Title
            const mainTitle = g.selectAll('.main-title').data([null])
            mainTitle
                .enter().append("text")
                .attr('class', 'main-title')
            .merge(mainTitle)
                .attr('fill', chartTheme.titleFill)
                .attr("x", (innerWidth / 2))             
                .attr("y", margin.top + 25)
                .attr("text-anchor", "middle")  
                .style("font-size", "26px") 
                .text("Distance vs. Time");


            // Polynomial Function
            const functionTitle = g.selectAll('.function').data([null])
            functionTitle
                .enter().append("text")
                .attr('class', 'function')
            .merge(functionTitle)
                .attr('fill', 'steelblue')
                .attr("x", innerWidth - 10)             
                .attr("y", 450)
                .attr("text-anchor", "end")  
                .style("font-size", "10px") 
                .style("overflow", "hidden")
                .text(polyFunction);


            // plot the line
            const lineGenerator = line()
                .x((value, index) => xScale(value.et))
                .y((value, index) => yScale(value.distance))
                .curve(curveBasis)

            g.selectAll('.line')
                .data([distanceTimeData])
                .join('path')
                .attr('class', 'line')
                .attr('d', value => lineGenerator(value))
                .attr('fill', 'none')
                .attr('stroke', 'red')
                .attr('stroke-width', '1')

            // draw the points
            g.selectAll('.dots')
                .data(distanceTimeData)
                .join("circle")
                .attr('class', 'dots')
                .attr('stroke','red')
                .attr('r','2')
                .attr('fill','red')
                .attr('cx', (d) => xScale(d.et))
                .attr('cy', (d) => yScale(d.distance))
                .append("svg:title")
                .text(function(d, i) { return `(${d.et}, ${d.distance})` });

            g.selectAll('.poly-line')
                .data([polyTimeData])
                .join('path')
                .attr('class', 'poly-line')
                .attr('d', value => lineGenerator(value))
                .attr('fill', 'none')
                .attr('stroke', 'steelblue')
                .attr('stroke-width', '1')

        }

        const RenderChart = () => {
            const svg = select(svgRef.current)

            responsiveComponent(svg, Object.assign({}, chartTheme, {
                width: window.innerWidth-20,
                height: 600,
                margin: { top: 20, bottom: 71, left: 100, right: 30 },
                xAxisLabel: 'Elapsed Time (s)',
                yAxisLabel: 'Distance (m)'
            }));
        }

        const debouncedHandleResize = debounce(function handleResize() {
            RenderChart()
        }, 500)

        window.addEventListener('resize', debouncedHandleResize)

        RenderChart()

        return _ => {
            window.removeEventListener('resize', debouncedHandleResize)
        }
    }, [])

    return ( 
        <>
            <AnalysisExport name={"Distance vs. Time"} data={distanceTimeData} />
            <svg ref={svgRef}></svg>
        </>
    );
}

export default DistanceVsTime;
